import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useError } from '../../../contexts/ErrorContext';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Typography,
  CircularProgress,
  Autocomplete,
  Box,
  Alert,
  Snackbar,
  Paper
} from '@mui/material';
import { Search, UserPlus, X, Users } from 'lucide-react';

import {
  searchUsers,
  addUserToGroup,
  removeUserFromGroup,
  fetchGroupDetails,
} from '../../../services/api';
import debounce from 'lodash/debounce';

/**
 * Dialog for managing group members
 */
const GroupMembersDialog = ({ 
  open, 
  onClose, 
  group, 
  token, 
  onSuccess
}) => {
  const { t } = useTranslation();
  const { showError } = useError();

  // State variables
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [members, setMembers] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Load current members when dialog opens
  useEffect(() => {
    if (open && group) {
      loadGroupMembers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, group]);

  // Load group members
  const loadGroupMembers = async () => {
    if (!group || !group.id) return;

    setIsLoading(true);
    try {
      const groupData = await fetchGroupDetails(token, group.id);
      setMembers(groupData.members || []);
    } catch (err) {
      const errorMsg = t('dashboard.groups.error.loading');
      setErrorMessage(errorMsg);
      showError(errorMsg);
      
    } finally {
      setIsLoading(false);
    }
  };

  // Debounced search function
  const debouncedSearch = useCallback((term) => {
    if (!term.trim()) return;

    setIsSearching(true);
    const searchPromise = async () => {
      try {
        const results = await searchUsers(token, term);
        // Filter out users who are already members
        const filteredResults = results.filter(
          user => !members.some(member => member.id === user.id)
        );
        setSearchResults(filteredResults);
      } catch (err) {
        const errorMsg = t('common.share_dialog.error.user_search_error');
        setErrorMessage(errorMsg);
        showError(errorMsg);
        
      } finally {
        setIsSearching(false);
      }
    };

    const debouncedFn = debounce(() => searchPromise(), 300);
    debouncedFn();

    return () => debouncedFn.cancel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, t, showError, members, setIsSearching, setSearchResults, setErrorMessage]);

  // Handle search term change
  useEffect(() => {
    let cancelSearch;
    if (searchTerm) {
      cancelSearch = debouncedSearch(searchTerm);
    }
    return () => {
      if (cancelSearch) {
        cancelSearch();
      }
    };
  }, [searchTerm, debouncedSearch]);

  // Add user to selection
  const handleSelectUser = (user) => {
    // Add user if not already selected
    if (!selectedUsers.find(u => u.id === user.id)) {
      setSelectedUsers([...selectedUsers, user]);
    }
    setSearchTerm('');
    setSearchResults([]);
  };

  // Remove user from selection
  const handleRemoveSelectedUser = (userId) => {
    setSelectedUsers(selectedUsers.filter(user => user.id !== userId));
  };

  // Remove user from group
  const handleRemoveUser = async (userId) => {
    try {
      await removeUserFromGroup(token, group.id, userId);
      await loadGroupMembers(); // Refresh the members list
      setSuccessMessage(t('dashboard.groups.member_removed'));
    } catch (err) {
      const errorMsg = t('dashboard.groups.error.removing_member');
      setErrorMessage(errorMsg);
      showError(errorMsg);
      
    }
  };

  // Save changes (add new users)
  const handleSave = async () => {
    if (selectedUsers.length === 0) {
      return; // Nothing to save
    }

    setIsSaving(true);
    try {
      // Add selected users to group
      const promises = selectedUsers.map(user => 
        addUserToGroup(token, group.id, user.id)
      );
      await Promise.all(promises);

      // Refresh members list
      await loadGroupMembers();

      // Clear selections
      setSelectedUsers([]);

      // Show success message
      setSuccessMessage(t('dashboard.groups.members_added'));

      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      const errorMsg = t('dashboard.groups.error.adding_members');
      setErrorMessage(errorMsg);
      showError(errorMsg);
      
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <Users size={20} />
          {t('dashboard.groups.manage_members')}
        </DialogTitle>

        <DialogContent sx={{ py: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('dashboard.groups.manage_members_description', { name: group?.name || '' })}
          </Typography>

          {/* Search field */}
          <Paper elevation={0} sx={{ mb: 3, borderRadius: '8px', bgcolor: 'background.paper', p: 2 }}>
            <Autocomplete
              freeSolo
              options={searchResults}
              getOptionLabel={(option) =>
                typeof option === 'string' ? option : option.username
              }
              renderOption={(props, option) => (
                <li {...props} key={option.id} onClick={() => handleSelectUser(option)}>
                  {option.username}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('dashboard.groups.search_users')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && debouncedSearch(searchTerm)}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isSearching ? (
                          <CircularProgress size={20} />
                        ) : (
                          <IconButton size="small" onClick={() => debouncedSearch(searchTerm)}>
                            <Search size={18} />
                          </IconButton>
                        )}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Paper>

          {/* Selected users */}
          {selectedUsers.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {t('dashboard.groups.users_to_add')}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {selectedUsers.map(user => (
                  <Button
                    key={user.id}
                    variant="outlined"
                    size="small"
                    endIcon={<X size={14} />}
                    onClick={() => handleRemoveSelectedUser(user.id)}
                    sx={{ mb: 1 }}
                  >
                    {user.username}
                  </Button>
                ))}
              </Box>
            </Box>
          )}

          {/* Current members */}
          <Paper 
            elevation={0} 
            sx={{ 
              borderRadius: '8px', 
              bgcolor: 'background.paper',
              border: '1px solid rgba(0, 0, 0, 0.12)',
              overflow: 'hidden'
            }}
          >
            {isLoading ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CircularProgress size={24} />
                <Typography variant="body2" sx={{ mt: 2 }}>
                  {t('dashboard.groups.loading_members')}
                </Typography>
              </Box>
            ) : (
              <Box>
                <Typography 
                  variant="subtitle2" 
                  sx={{ 
                    p: 2, 
                    borderBottom: members.length > 0 ? '1px solid rgba(0, 0, 0, 0.12)' : 'none'
                  }}
                >
                  {t('dashboard.groups.current_members')}
                </Typography>
                {members.length > 0 ? (
                  <List disablePadding>
                    {members.map(member => (
                      <ListItem key={member.id} divider>
                        <ListItemText
                          primary={member.username || 'Unknown user'}
                          secondary={member.email || ''}
                        />
                        {/* Don't allow removing the owner */}
                        {group?.owner?.id !== member.id && (
                          <ListItemSecondaryAction>
                            <IconButton
                              edge="end"
                              onClick={() => handleRemoveUser(member.id)}
                              size="small"
                            >
                              <X size={16} />
                            </IconButton>
                          </ListItemSecondaryAction>
                        )}
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Box sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('dashboard.groups.no_members')}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Paper>
        </DialogContent>

        <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(0, 0, 0, 0.12)' }}>
          <Button 
            onClick={onClose}
            variant="outlined"
            disabled={isSaving}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={selectedUsers.length === 0 || isSaving}
            startIcon={isSaving ? <CircularProgress size={16} /> : <UserPlus size={16} />}
          >
            {t('dashboard.groups.add_members')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success message */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" elevation={6} variant="filled">
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Error message */}
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={5000}
        onClose={() => setErrorMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" elevation={6} variant="filled">
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

GroupMembersDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  group: PropTypes.object.isRequired,
  token: PropTypes.string.isRequired,
  onSuccess: PropTypes.func
};

export default GroupMembersDialog;
